import React, { useState, useMemo, useEffect, useRef } from "react";
import { Copy, Check, ClipboardList, Settings2, Info, FileCheck, Download, ZoomIn } from "lucide-react";
import { useZoom } from "./ZoomContext";

/**
 * parseToolReply(raw)
 * ----------------------------------------------------------------------------
 * Turns a dual-format reply (from Product Page Generator / Store Copy Pack)
 * into a list of UI cards plus the optional structured JSON.
 *
 * Format the model produces:
 *   ===== PLAIN TEXT (paste these into Shopify) =====
 *   === COPY → <where to paste> ===
 *   <body text>
 *   === END ===
 *   ... more blocks ...
 *   ===== JSON (for the system) =====   (optional)
 *   ```json
 *   { ... }
 *   ```
 *
 * Card types:
 *   "copy"    -> show a Copy button (heading = where to paste it)
 *   "fields"  -> short fields the user types (info card, no Copy button)
 *   "toggles" -> settings to switch on (info card)
 *   "review"  -> "please review" note (info card)
 *   "info"    -> anything else (info card)
 *
 * All "=====" / "=== END ===" markers are stripped before rendering.
 */
export function parseToolReply(raw) {
  const text = String(raw || "");

  // 0) JSON-only reply (e.g. Static Ad Studio "VALID JSON ONLY" contract).
  //    Build one card per top-level field with friendly destination labels.
  if (isJsonOnlyReply(text)) {
    const stripped = text.trim().replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "");
    let obj = null;
    try {
      obj = JSON.parse(stripped);
    } catch {
      obj = null;
    }
    return { cards: obj ? fieldCardsFromJson(obj) : [], data: obj };
  }

  // 1) Split the PLAIN TEXT half from the JSON half.
  const plainMarker = /=====\s*PLAIN TEXT[^=]*=====/i;
  const jsonMarker = /=====\s*JSON[^=]*=====/i;

  let plainPart = text;
  let jsonPart = "";

  const jsonMatch = text.match(jsonMarker);
  if (jsonMatch) {
    plainPart = text.slice(0, jsonMatch.index);
    jsonPart = text.slice(jsonMatch.index + jsonMatch[0].length);
  }
  plainPart = plainPart.replace(plainMarker, ""); // drop the PLAIN TEXT header line

  // Drop any stray triple-backtick code fences that may wrap the marker lines.
  plainPart = plainPart.replace(/```[^\n]*\n?/g, "");

  // 2) Parse the structured JSON (tolerant of code fences).
  const data = jsonPart ? extractJson(jsonPart) : null;

  // 3) Break the plain part into blocks on "=== END ===".
  const cards = [];
  const segments = plainPart.split(/===\s*END\s*===/i);

  for (const segment of segments) {
    const trimmed = segment.trim();
    if (!trimmed) continue;

    // The header is the first "=== ... ===" line that isn't END.
    const lines = trimmed.split("\n");
    let headerIdx = -1;
    let rawHeader = "";
    for (let i = 0; i < lines.length; i++) {
      const m = lines[i].match(/^\s*===\s*(.+?)\s*===\s*$/);
      if (m && !/^END$/i.test(m[1].trim())) {
        headerIdx = i;
        rawHeader = m[1].trim();
        break;
      }
    }
    if (headerIdx === -1) continue;

    const body = lines.slice(headerIdx + 1).join("\n").trim();
    cards.push(classifyBlock(rawHeader, body));
  }

  return { cards, data };
}

function classifyBlock(rawHeader, body) {
  const cleanHeader = rawHeader.replace(/\s*\(no copy needed\)\s*/i, "").trim();
  const upper = cleanHeader.toUpperCase();

  if (upper.startsWith("COPY")) {
    // "COPY → <destination>" -> heading is the destination
    const dest = cleanHeader.replace(/^COPY\s*(→|->|:)?\s*/i, "").trim();
    return { type: "copy", heading: dest, body, copyable: true, copyText: body };
  }
  if (upper.startsWith("TYPE")) {
    return { type: "fields", heading: cleanHeader, body, copyable: false };
  }
  if (upper.startsWith("SET")) {
    return { type: "toggles", heading: cleanHeader, body, copyable: false };
  }
  if (upper.includes("REVIEW")) {
    return { type: "review", heading: cleanHeader, body, copyable: false };
  }
  return { type: "info", heading: cleanHeader, body, copyable: false };
}

function extractJson(s) {
  let candidate = null;
  const fence = s.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence) {
    candidate = fence[1];
  } else {
    const first = s.indexOf("{");
    const last = s.lastIndexOf("}");
    if (first !== -1 && last > first) candidate = s.slice(first, last + 1);
  }
  if (!candidate) return null;
  try {
    return JSON.parse(candidate.trim());
  } catch {
    return null;
  }
}

/**
 * Heuristic detector — if the assistant text contains the dual-format markers
 * we render it with <ToolOutput>; otherwise fall back to <Markdown>.
 */
export function isToolOutput(raw) {
  const t = String(raw || "");
  if (isJsonOnlyReply(t)) return true;
  if (!/=====\s*PLAIN TEXT/i.test(t)) return false;
  if (!/===\s*END\s*===/i.test(t)) return false;
  return true;
}

/**
 * Pure-JSON reply detector — Static Ad Studio and similar generators that
 * are contracted to "VALID JSON ONLY". We tolerate a single optional code
 * fence around the JSON.
 */
export function isJsonOnlyReply(raw) {
  const t = String(raw || "").trim();
  if (!t) return false;
  // Strip an optional ```json ... ``` fence
  const fence = t.match(/^```(?:json)?\s*([\s\S]*?)```\s*$/i);
  const candidate = (fence ? fence[1] : t).trim();
  if (!candidate.startsWith("{") || !candidate.endsWith("}")) return false;
  try {
    const obj = JSON.parse(candidate);
    return obj && typeof obj === "object" && !Array.isArray(obj);
  } catch {
    return false;
  }
}

/**
 * Maps a JSON-only reply to renderable cards. Known schemas get friendly
 * labels and destination hints (e.g. Static Ad Studio); unknown keys fall
 * back to a sensible "Title-Case" label.
 *
 * Card categories:
 *   "copy" -> rendered with a Copy button (heading = where to paste it)
 *   "info" -> rendered without a Copy button (context / metadata)
 */
function fieldCardsFromJson(obj) {
  // The four classic Meta-ad copy fields are the ones the participant
  // pastes straight into Ads Manager, plus the image_prompt that goes to
  // the image model. Everything else is supporting context.
  const COPY_FIELDS = new Set([
    "primary_text",
    "headline",
    "description",
    "cta",
    "image_prompt",
    "caption",
    "voiceover", // when serialised at row level
  ]);
  // Keys consumed by the dedicated AdImageCard — skip from regular iteration.
  const IMAGE_INTERNAL_KEYS = new Set([
    "image_base64",
    "image_mime",
    "image_error",
    // Static Ad Studio v2 — handled by VariationCard, not top-level cards.
    "variations",
    "discovery_summary",
  ]);
  // Friendly destination labels (used as the card heading) when we know them.
  const LABELS = {
    // Static Ad Studio
    angle_rationale: "Angle rationale",
    primary_text: "Ads Manager › Primary text",
    headline: "Ads Manager › Headline (≤ 40 chars)",
    description: "Ads Manager › Description",
    cta: "Ads Manager › Call to action button",
    image_prompt: "Paste into Gemini / image model",
    aspect_ratio: "Aspect ratio",
    overlay_zone: "Overlay zone (where to place the headline)",
    // Angle Engine
    market_read: "Market read",
    recommended_first_angle: "Recommended first angle to test",
    angles: "Selling angles",
    // Audience Architect
    usage_note: "How to use these audiences",
    interests: "Meta interest audiences",
    // Ad Script Studio
    hook_variations: "Hook variations to test",
    script: "Video script (beat-by-beat)",
    caption: "Facebook post primary text",
    estimated_length: "Estimated length",
    sourcing_note: "Sourcing recommendation",
  };

  const titleCase = (key) =>
    key
      .split("_")
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ");

  const cards = [];

  for (const [key, value] of Object.entries(obj || {})) {
    if (value === null || value === undefined || value === "") continue;
    if (IMAGE_INTERNAL_KEYS.has(key)) continue;
    const heading = LABELS[key] || titleCase(key);

    // String -> single card
    if (typeof value === "string") {
      cards.push({
        type: COPY_FIELDS.has(key) ? "copy" : "info",
        heading,
        body: value,
        copyable: COPY_FIELDS.has(key),
        copyText: value,
      });
      continue;
    }

    // Array of strings -> one card with newline-joined body + Copy
    if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
      const body = value.filter(Boolean).map((v) => `• ${v}`).join("\n");
      if (!body) continue;
      cards.push({
        type: "copy",
        heading,
        body,
        copyable: true,
        copyText: value.filter(Boolean).join("\n\n"),
      });
      continue;
    }

    // Array of objects -> one card per row with a pretty-printed body + Copy
    if (Array.isArray(value) && value.every((v) => v && typeof v === "object")) {
      value.forEach((row, idx) => {
        const lines = Object.entries(row)
          .filter(([, v]) => v !== null && v !== undefined && v !== "")
          .map(([k, v]) => `${titleCase(k)}: ${typeof v === "string" ? v : JSON.stringify(v)}`);
        if (!lines.length) return;
        const rowHeading = `${heading} — #${idx + 1}${row.angle_name ? ` · ${row.angle_name}` : row.keyword ? ` · ${row.keyword}` : row.beat ? ` · ${row.beat}` : ""}`;
        cards.push({
          type: "copy",
          heading: rowHeading,
          body: lines.join("\n"),
          copyable: true,
          copyText: lines.join("\n"),
        });
      });
      continue;
    }

    // Plain object fallback -> pretty-print
    cards.push({
      type: "info",
      heading,
      body: JSON.stringify(value, null, 2),
      copyable: false,
    });
  }
  return cards;
}

// ---------------------------------------------------------------------------
// UI — premium black + antique-gold cards matching the hub aesthetic
// ---------------------------------------------------------------------------

function CopyButton({ text, label = "Copy", testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API blocked (rare). Fall back to a selection prompt.
      window.prompt("Copy this with Cmd/Ctrl+C:", text);
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testid}
      className={[
        "inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11px] uppercase tracking-[0.16em] font-bold transition-all border-[1.5px]",
        copied
          ? "bg-[#b89846] text-black border-[#b89846]"
          : "border-[#b89846]/45 text-[#e8c879] hover:bg-[#b89846] hover:text-black hover:border-[#b89846]",
      ].join(" ")}
      aria-label={copied ? "Copied" : "Copy to clipboard"}
    >
      {copied ? <Check size={13} strokeWidth={2.4} /> : <Copy size={13} strokeWidth={2.2} />}
      {copied ? "Copied" : label}
    </button>
  );
}

function CardIcon({ type }) {
  const cls = "text-[#e8c879]";
  switch (type) {
    case "copy":
      return <Copy size={14} className={cls} strokeWidth={2} />;
    case "fields":
      return <ClipboardList size={14} className={cls} strokeWidth={2} />;
    case "toggles":
      return <Settings2 size={14} className={cls} strokeWidth={2} />;
    case "review":
      return <FileCheck size={14} className={cls} strokeWidth={2} />;
    default:
      return <Info size={14} className={cls} strokeWidth={2} />;
  }
}

function ToolCard({ card, index }) {
  const label = card.type === "copy" ? "Paste this into" : "Step";
  return (
    <div
      data-testid={`tool-card-${index}`}
      className="rounded-2xl border border-[#b89846]/35 bg-[#0F0F0E] overflow-hidden shadow-[0_8px_28px_-14px_rgba(184,152,70,0.35)]"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-4 md:px-5 py-3 border-b border-[#b89846]/20 bg-[#141210]">
        <div className="flex items-start gap-2 min-w-0">
          <CardIcon type={card.type} />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#b89846]/75 font-bold">
              {label}
            </div>
            <div className="text-sm md:text-[15px] text-white font-medium leading-snug break-words">
              {card.heading}
            </div>
          </div>
        </div>
        {card.copyable && (
          <CopyButton text={card.copyText} testid={`tool-card-${index}-copy`} />
        )}
      </div>
      {/* Body */}
      {card.body && (
        <pre className="px-4 md:px-5 py-3 text-[13px] md:text-sm text-white/85 whitespace-pre-wrap break-words font-sans leading-relaxed">
          {card.body}
        </pre>
      )}
    </div>
  );
}

/**
 * AdImageCard — renders the Gemini-generated static ad image returned by the
 * Static Ad Studio backend. Supports click-to-zoom (via ZoomContext) and a
 * one-click PNG download.
 */
function AdImageCard({ base64, mime = "image/png", aspectRatio, headline, autoZoomKey }) {
  const { openZoom } = useZoom();
  const autoZoomedFor = useRef(null);
  const dataUri = `data:${mime};base64,${base64}`;

  const escapeHtml = (s) =>
    String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const openInZoom = () => {
    const html = `<img src="${dataUri}" alt="${escapeHtml(headline || "Generated ad image")}" style="max-width: min(80vw, 1200px); max-height: 80vh; width: auto; height: auto; display: block; border-radius: 8px;" />`;
    openZoom(html);
  };

  // Auto-open the zoom modal once per image (keyed by autoZoomKey, which is
  // a hash of the base64 — so editing the chat or re-rendering doesn't
  // re-trigger the modal, but a brand-new generation does). NOTE: we MUST
  // assign the dedupe ref INSIDE the setTimeout callback, not before
  // scheduling it. React.StrictMode runs effects twice in dev (mount →
  // cleanup → mount); if we set the ref eagerly the second mount sees the
  // ref already set, returns early, and the modal never opens.
  useEffect(() => {
    if (!autoZoomKey) return;
    if (autoZoomedFor.current === autoZoomKey) return;
    const t = setTimeout(() => {
      autoZoomedFor.current = autoZoomKey;
      openInZoom();
    }, 220);
    return () => clearTimeout(t);
  }, [autoZoomKey]);

  const onDownload = (e) => {
    e.stopPropagation();
    const a = document.createElement("a");
    a.href = dataUri;
    a.download = `static-ad-${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div
      data-testid="ad-image-card"
      className="rounded-2xl border border-[#b89846]/35 bg-[#0F0F0E] overflow-hidden shadow-[0_8px_28px_-14px_rgba(184,152,70,0.35)]"
    >
      <div className="flex items-start justify-between gap-3 px-4 md:px-5 py-3 border-b border-[#b89846]/20 bg-[#141210]">
        <div className="flex items-start gap-2 min-w-0">
          <ZoomIn size={14} className="text-[#e8c879]" strokeWidth={2} />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#b89846]/75 font-bold">
              Generated by Gemini Nano Banana
            </div>
            <div className="text-sm md:text-[15px] text-white font-medium leading-snug break-words">
              Your ad creative{aspectRatio ? ` · ${aspectRatio}` : ""}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onDownload}
          data-testid="ad-image-download-btn"
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11px] uppercase tracking-[0.16em] font-bold transition-all border-[1.5px] border-[#b89846]/45 text-[#e8c879] hover:bg-[#b89846] hover:text-black hover:border-[#b89846]"
          aria-label="Download generated image"
        >
          <Download size={13} strokeWidth={2.2} />
          PNG
        </button>
      </div>
      <button
        type="button"
        onClick={openInZoom}
        data-testid="ad-image-zoom-trigger"
        className="block w-full bg-black hover:bg-[#0a0a09] transition group relative"
        aria-label="Click to zoom generated ad image"
      >
        <img
          src={dataUri}
          alt={headline || "Generated ad image"}
          className="w-full h-auto max-h-[520px] object-contain mx-auto"
        />
        <span className="absolute top-2 right-2 inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-black/70 backdrop-blur-sm border border-[#b89846]/40 text-[10px] uppercase tracking-[0.16em] font-bold text-[#e8c879] opacity-0 group-hover:opacity-100 transition">
          <ZoomIn size={12} strokeWidth={2.2} />
          Zoom
        </span>
      </button>
    </div>
  );
}

/**
 * VariationCard — renders ONE Static Ad Studio v2 variation: its image
 * (or error), its on-image text reference, and its copy cards. No auto-zoom
 * (because multiple variations stack — user clicks to zoom any one).
 */
function VariationCard({ variation, index }) {
  const v = variation || {};
  const hasImage = typeof v.image_base64 === "string" && v.image_base64.length > 0;
  const imageError = typeof v.image_error === "string" ? v.image_error : "";
  const mime = typeof v.image_mime === "string" ? v.image_mime : "image/png";
  const headline = typeof v.headline === "string" ? v.headline : "";
  const aspectRatio = typeof v.aspect_ratio === "string" ? v.aspect_ratio : "";
  const name = typeof v.variation_name === "string" && v.variation_name ? v.variation_name : `Variation ${index + 1}`;

  // Build the copy cards for this variation from the same JSON helper, but
  // skip the keys we render in their own slots above.
  const cardSubset = useMemo(() => {
    const subset = { ...v };
    delete subset.image_base64;
    delete subset.image_mime;
    delete subset.image_error;
    delete subset.variation_name; // shown in header
    delete subset.on_image_text; // shown in its own row above the copy cards
    return fieldCardsFromJson(subset);
  }, [v]);

  return (
    <section
      data-testid={`ad-variation-${index}`}
      data-variation-name={name}
      className="rounded-2xl border border-[#b89846]/40 bg-[#0B0B0A] overflow-hidden"
    >
      <header className="flex items-center justify-between gap-3 px-4 md:px-5 py-3 border-b border-[#b89846]/25 bg-[#141210]">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex h-6 min-w-6 px-2 items-center justify-center rounded-full bg-[#b89846] text-black text-[11px] font-bold">
            {index + 1}
          </span>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#b89846]/75 font-bold">
              Variation
            </div>
            <div className="text-sm md:text-[15px] text-white font-semibold leading-snug truncate">
              {name}
            </div>
          </div>
        </div>
        {aspectRatio ? (
          <span className="shrink-0 text-[10px] uppercase tracking-[0.18em] text-[#e8c879]/80 font-bold border border-[#b89846]/35 rounded-full px-2.5 py-1">
            {aspectRatio}
          </span>
        ) : null}
      </header>

      <div className="p-4 md:p-5 space-y-3 md:space-y-4">
        {hasImage ? (
          <AdImageCard
            base64={v.image_base64}
            mime={mime}
            aspectRatio={aspectRatio}
            headline={headline}
            // No auto-zoom for multi-variation flow — user clicks to zoom.
            autoZoomKey=""
          />
        ) : imageError ? (
          <div
            data-testid={`ad-variation-${index}-error`}
            className="rounded-2xl border border-red-500/30 bg-red-950/20 px-4 md:px-5 py-3"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-red-300/80 font-bold mb-1">
              Image generation skipped
            </div>
            <div className="text-sm text-red-100/90 leading-relaxed">
              {imageError}
            </div>
            <div className="text-[12px] text-red-200/70 mt-2">
              You can still copy the image_prompt below into any image model manually.
            </div>
          </div>
        ) : null}

        {v.on_image_text && typeof v.on_image_text === "object" ? (
          <div
            data-testid={`ad-variation-${index}-on-image-text`}
            className="rounded-2xl border border-[#b89846]/25 bg-[#0F0F0E] px-4 md:px-5 py-3"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#b89846]/75 font-bold mb-2">
              Text baked into the image
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-[13px]">
              {Object.entries(v.on_image_text)
                .filter(([, val]) => val && String(val).trim())
                .map(([k, val]) => (
                  <div key={k} className="flex flex-col">
                    <dt className="text-[10px] uppercase tracking-[0.18em] text-[#b89846]/65 font-bold">
                      {k.replace(/_/g, " ")}
                    </dt>
                    <dd className="text-white/90 leading-snug break-words">{String(val)}</dd>
                  </div>
                ))}
            </dl>
          </div>
        ) : null}

        {cardSubset.length ? (
          <div className="space-y-2.5">
            {cardSubset.map((card, i) => (
              <ToolCard key={i} card={card} index={i} />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default function ToolOutput({ reply }) {
  const { cards, data } = useMemo(() => parseToolReply(reply), [reply]);

  // Static Ad Studio v2 — `variations` array drives a multi-section render.
  const variations = data && Array.isArray(data.variations) ? data.variations : null;
  const discoverySummary = data && typeof data.discovery_summary === "string"
    ? data.discovery_summary
    : "";

  // Detect Static Ad Studio v1 single image (legacy fallback).
  const imageBase64 = data && typeof data.image_base64 === "string" ? data.image_base64 : "";
  const imageMime = data && typeof data.image_mime === "string" ? data.image_mime : "image/png";
  const aspectRatio = data && typeof data.aspect_ratio === "string" ? data.aspect_ratio : "";
  const headline = data && typeof data.headline === "string" ? data.headline : "";
  const imageError = data && typeof data.image_error === "string" ? data.image_error : "";
  const autoZoomKey = imageBase64 ? imageBase64.slice(0, 32) : "";

  // Build a slim copy of the structured JSON for the developer-only panel,
  // stripping every base64 payload (top-level + per-variation) so the dump
  // stays readable. Computed unconditionally so the hook order is stable.
  const slimData = useMemo(() => {
    if (!data) return null;
    const out = JSON.parse(JSON.stringify(data));
    if (typeof out.image_base64 === "string" && out.image_base64) {
      out.image_base64 = `[base64 image · ${out.image_base64.length} chars · use Download PNG]`;
    }
    if (Array.isArray(out.variations)) {
      out.variations = out.variations.map((v) => {
        if (v && typeof v.image_base64 === "string" && v.image_base64) {
          return { ...v, image_base64: `[base64 image · ${v.image_base64.length} chars · use Download PNG]` };
        }
        return v;
      });
    }
    return out;
  }, [data]);

  // Safety net — if nothing parsed AND no image AND no error AND no
  // variations, fall back to plain text so the user still sees the content
  // (never an empty bubble).
  if (!cards.length && !imageBase64 && !imageError && (!variations || !variations.length)) {
    return (
      <pre className="whitespace-pre-wrap break-words text-sm text-white/85 font-sans leading-relaxed">
        {String(reply || "")}
      </pre>
    );
  }

  return (
    <div
      className="space-y-3 md:space-y-4"
      data-testid="tool-output"
      data-card-count={cards.length}
      data-has-image={imageBase64 ? "true" : "false"}
      data-variations-count={variations ? variations.length : 0}
    >
      {/* VARIATIONS MODE (Static Ad Studio v2) */}
      {variations && variations.length ? (
        <>
          {discoverySummary ? (
            <div
              data-testid="ad-discovery-summary"
              className="rounded-2xl border border-[#b89846]/30 bg-[#0F0F0E] px-4 md:px-5 py-3"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#b89846]/75 font-bold mb-1">
                Brief locked in
              </div>
              <div className="text-sm text-white/90 leading-relaxed">
                {discoverySummary}
              </div>
            </div>
          ) : null}
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#b89846]/85 font-bold">
            {variations.length} ad variation{variations.length === 1 ? "" : "s"} · click any image to zoom · download as PNG
          </div>
          {variations.map((v, i) => (
            <VariationCard key={i} variation={v} index={i} />
          ))}
        </>
      ) : (
        <>
          {/* SINGLE-IMAGE MODE (legacy v1 + generic JSON tools) */}
          {imageBase64 ? (
            <AdImageCard
              base64={imageBase64}
              mime={imageMime}
              aspectRatio={aspectRatio}
              headline={headline}
              autoZoomKey={autoZoomKey}
            />
          ) : imageError ? (
            <div
              data-testid="ad-image-error"
              className="rounded-2xl border border-red-500/30 bg-red-950/20 px-4 md:px-5 py-3"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-red-300/80 font-bold mb-1">
                Image generation skipped
              </div>
              <div className="text-sm text-red-100/90 leading-relaxed">
                {imageError}
              </div>
              <div className="text-[12px] text-red-200/70 mt-2">
                You can still copy the `image_prompt` below into any image model manually.
              </div>
            </div>
          ) : null}
          {cards.length ? (
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#b89846]/85 font-bold">
              {cards.length} block{cards.length === 1 ? "" : "s"} · click Copy to paste straight into Shopify
            </div>
          ) : null}
          {cards.map((card, i) => (
            <ToolCard key={i} card={card} index={i} />
          ))}
        </>
      )}

      {/* Developer JSON panel — works for both modes, base64 stripped. */}
      {slimData ? (
        <details className="rounded-2xl border border-[#b89846]/20 bg-[#0F0F0E] overflow-hidden">
          <summary className="cursor-pointer select-none px-4 md:px-5 py-3 text-[11px] uppercase tracking-[0.18em] text-[#b89846]/80 font-bold hover:bg-[#141210]">
            Structured JSON (for developers)
          </summary>
          <div className="px-4 md:px-5 pb-4 pt-2">
            <div className="flex justify-end mb-2">
              <CopyButton
                text={JSON.stringify(slimData, null, 2)}
                testid="tool-output-json-copy"
                label="Copy JSON"
              />
            </div>
            <pre className="text-[12px] text-white/80 whitespace-pre-wrap break-words font-mono leading-relaxed max-h-72 overflow-auto">
              {JSON.stringify(slimData, null, 2)}
            </pre>
          </div>
        </details>
      ) : null}
    </div>
  );
}
